import { ReactNode, useEffect, useState } from "react";
import { QRCode } from "antd";
import { useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowUpRight,
  faCircleCheck,
  faCircleExclamation,
  faEnvelopeOpen,
  faKey,
  faMobile,
} from "@fortawesome/pro-light-svg-icons";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import userAppLogo from "../image/user-app.svg";
import DeepLinkLanguageSwitcher from "./DeepLinkLanguageSwitcher";
import { resolveLanguage } from "../i18n";
import { useChangeLanguage } from "../containers/Topbar/useLanguageMenu";
import { showHtml } from "../utils/showHtml";

type DeepLinkStepCardProps = {
  title: string;
  bullets?: string[];
  icon: IconDefinition;
  renderFooter?: ReactNode;
  compact?: boolean;
};

function DeepLinkStepCard({ title, bullets, icon, renderFooter, compact }: DeepLinkStepCardProps) {
  return (
    <div
      className={`bg-white rounded-2xl p-5 min-[62rem]:p-6 shadow-md w-full max-w-[420px] ${compact ? "md:w-[340px] xxl:w-[380px]" : "xl:max-w-none"}`}
    >
      <div className="flex items-center gap-3.5 mb-4 min-[62rem]:gap-4 min-[62rem]:mb-5">
        <div
          className="shrink-0 w-12 h-12 min-[62rem]:w-[52px] min-[62rem]:h-[52px] rounded-full flex items-center justify-center text-primary"
          style={{ backgroundColor: "#E6F7EF" }}
        >
          <FontAwesomeIcon aria-hidden="true" icon={icon} size="lg" />
        </div>
        <div className="text-lg font-bold text-[#141414]">{title}</div>
      </div>
      {bullets && bullets.length > 0 && (
        <ul className="text-[#5C5C5C] text-base pl-5 space-y-1.5 list-disc">
          {bullets.map((bullet) => (
            <li key={bullet}>{bullet}</li>
          ))}
        </ul>
      )}
      {renderFooter && <div className="mt-4 min-[62rem]:mt-5">{renderFooter}</div>}
    </div>
  );
}

type Device = { platform: "android" | "ios" | "desktop"; isPhone: boolean };

function detectDevice(): Device {
  const userAgent = navigator.userAgent || navigator.vendor || "";
  if (/android/i.test(userAgent)) return { platform: "android", isPhone: /mobile/i.test(userAgent) };
  if (/iPad|iPhone|iPod/.test(userAgent)) return { platform: "ios", isPhone: !/iPad/.test(userAgent) };
  return { platform: "desktop", isPhone: false };
}

export default function DeepLink() {
  const { lang } = useParams<{ lang: string }>();
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const changeLanguage = useChangeLanguage();
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  const [{ platform, isPhone }] = useState(detectDevice);

  useEffect(() => {
    if (lang) {
      const resolved = resolveLanguage(lang as any);
      if (resolved !== i18n.language) {
        changeLanguage(resolved);
      }
    }
  }, [lang]);

  const code = searchParams.get("code");
  const hasCode = !!code;
  const isValidCode = !!code && /^[a-zA-Z0-9]{12}$/.test(code);
  const isInvalidCode = hasCode && !isValidCode;
  const showCodeInHeader = isInvalidCode && code.length <= 20;
  const isAnonymous = searchParams.get("anonymous") === "1";
  const deepLinkQuery = isAnonymous ? "anonymous=1" : isValidCode ? `code=${code}` : "";
  const useExpandedLayout = !isValidCode && !isAnonymous;

  const androidUrl = deepLinkQuery
    ? `https://play.google.com/store/apps/details?id=com.emz.egate.digi.userapp&referrer=${encodeURIComponent(deepLinkQuery)}`
    : "https://play.google.com/store/apps/details?id=com.emz.egate.digi.userapp";
  const iosUrl = "https://apps.apple.com/app/egate-digi/id1497026032";

  const copyButtonText = copied ? t("Copied!") : copyFailed ? t("Failed") : t("Copy");

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code!);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      setCopyFailed(true);
      setTimeout(() => setCopyFailed(false), 2000);
    }
  };

  const showAndroidButton = platform === "android" || platform === "desktop";
  const showIosButton = platform === "ios" || platform === "desktop";

  const storeButtons = (
    <div className="flex flex-wrap gap-2.5 items-center min-[62rem]:gap-2">
      {showAndroidButton && (
        <a href={androidUrl} rel="noopener noreferrer" target="_blank">
          {/* the Google badge image has transparent padding around it, the negative margin trims it */}
          <img
            alt={t("Get it on Google Play")}
            className="h-[72px] max-w-none -m-3 min-[62rem]:h-10 min-[62rem]:m-0"
            src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
          />
        </a>
      )}
      {showIosButton && (
        <a href={iosUrl} rel="noopener noreferrer" target="_blank">
          <img
            alt={t("Download on the App Store")}
            className="h-12 min-[62rem]:h-7"
            src="https://developer.apple.com/app-store/marketing/guidelines/images/badge-download-on-the-app-store.svg"
          />
        </a>
      )}
    </div>
  );

  const canOpenInApp = platform !== "desktop";
  const openInAppUrl = `emz-userapp://activate-contract?${deepLinkQuery}`;

  const showCodePanel = isValidCode && !isAnonymous;
  const showQrPanel = !isPhone;
  const heroSubtitle = isAnonymous
    ? t("Continue in anonymous mode from the app login screen.")
    : isValidCode
      ? t("Use the code below during registration to activate your account!")
      : "";

  const steps = [
    {
      title: t("Install the app"),
      bullets: [t("Download the eGate Digi app"), t("Available on Google Play and the App Store")],
      icon: faMobile,
      renderFooter: storeButtons,
    },
    isAnonymous
      ? {
          title: t("Use anonymous mode"),
          bullets: [t('Open the app and tap "Continue in anonymous mode"'), t("No contract code needed")],
          icon: faKey,
          renderFooter: canOpenInApp ? (
            <a
              className="bg-primary text-white border-none py-3 px-6 rounded-lg font-bold cursor-pointer no-underline block text-center"
              href={openInAppUrl}
            >
              {t("Open in app")}
            </a>
          ) : undefined,
        }
      : isValidCode
        ? {
            title: t("Enter activation code"),
            bullets: [
              t("MobileAppInstall.useCodeAbove", "Use the code above to register"),
              t("Enter it during account creation"),
            ],
            icon: faKey,
          }
        : isInvalidCode
          ? {
              title: t("Get a valid code"),
              bullets: [
                t("The provided code is not valid"),
                t("Contact your service provider or municipality for a correct code"),
                t("The code contains 12 characters (e.g. AB12CD34EF56)"),
              ],
              icon: faEnvelopeOpen,
            }
          : {
              title: t("Prepare your contract code"),
              bullets: [
                t("Have your contract code ready"),
                t("The code contains 12 characters"),
                t("You will enter it during registration"),
                t("Provided by your service provider or municipality"),
              ],
              icon: faKey,
            },
    ...(isInvalidCode
      ? [
          {
            title: t("Enter code manually"),
            bullets: [t("Open the app and enter the code when prompted during registration")],
            icon: faKey,
          },
          {
            title: t("You are all set!"),
            bullets: [
              t("Your account will be activated after entering a valid code"),
              t("You can then use the app to open containers"),
            ],
            icon: faCircleCheck,
          },
        ]
      : useExpandedLayout
        ? [
            {
              title: t("Find your code"),
              bullets: [
                t("Check your email or letter from your service provider or municipality"),
                t("If you cannot find the code, contact your provider"),
                t("Registration is required unless anonymous access is supported"),
              ],
              icon: faEnvelopeOpen,
            },
            {
              title: t("You are all set!"),
              bullets: [
                t("Enter the contract code during registration"),
                t("Your account will be activated"),
                t("You can then use the app to open containers"),
              ],
              icon: faCircleCheck,
            },
          ]
        : [
            {
              title: t("You are all set!"),
              bullets: [
                ...(isAnonymous ? [] : [t("Your contract is now linked")]),
                t("You can use the app to open containers"),
              ],
              icon: faCircleCheck,
            },
          ]),
  ];

  return (
    <>
      {/* Tailwind CDN */}
      <script src="https://cdn.tailwindcss.com"></script>

      <div
        className="min-h-screen bg-white"
        style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif" }}
      >
        {/* Language Switch */}
        <div className="absolute top-3 right-3 min-[62rem]:top-4 min-[62rem]:right-4 z-10">
          <DeepLinkLanguageSwitcher />
        </div>

        {/* Hero Section */}
        <section className="text-white bg-primary px-4 pt-16 pb-9 min-[62rem]:px-5 min-[62rem]:pt-10 min-[62rem]:pb-12">
          <div className="max-w-[1000px] mx-auto">
            <div className="flex flex-col-reverse items-center gap-4 min-[62rem]:flex-row min-[62rem]:justify-between min-[62rem]:gap-8">
              <div className="max-[62rem]:text-center min-[62rem]:max-w-[540px]">
                <h1 className="text-[length:clamp(28px,9.24vw,36px)] leading-[1.12] font-extrabold min-[62rem]:text-[48px]">
                  {t("Manage your waste with eGate Digi")}
                </h1>
                {heroSubtitle && (
                  <p className="text-[17px] leading-[1.5] mt-3 min-[62rem]:text-xl min-[62rem]:leading-[30px] min-[62rem]:mt-3.5">
                    {heroSubtitle}
                  </p>
                )}
                {isInvalidCode && (
                  <div
                    className="inline-flex flex-col bg-white rounded-[10px] overflow-hidden border-2 mt-6"
                    style={{ borderColor: "#ef4444" }}
                  >
                    {showCodeInHeader && (
                      <div className="px-6 py-3 text-gray-900 font-bold text-lg text-center uppercase min-w-[120px]">
                        {code}
                      </div>
                    )}
                    <div
                      className="flex items-center gap-2 px-3 py-2.5 text-sm"
                      style={{
                        backgroundColor: "#fef2f2",
                        borderTop: showCodeInHeader ? "1px solid #fee2e2" : undefined,
                        color: "#b91c1c",
                      }}
                    >
                      <FontAwesomeIcon className="shrink-0" icon={faCircleExclamation} />
                      <span>{t("Invalid code. Please contact your service provider.")}</span>
                    </div>
                  </div>
                )}
              </div>
              <div className="shrink-0 bg-white rounded-full" style={{ padding: "6px" }}>
                <img alt={t("eGate Digi")} className="w-[98px] min-[62rem]:w-[164px] h-auto block" src={userAppLogo} />
              </div>
            </div>

            {(showCodePanel || showQrPanel) && (
              <div
                className={`mt-6 mx-auto w-full max-w-[420px] bg-white rounded-2xl shadow-lg flex-col min-[62rem]:mt-7 min-[62rem]:mx-0 min-[62rem]:w-fit min-[62rem]:max-w-full min-[62rem]:flex-row ${showCodePanel ? "flex" : "hidden min-[62rem]:flex"}`}
              >
                {showCodePanel && (
                  <div className="min-w-0 flex flex-col justify-center p-5 min-[62rem]:py-6 min-[62rem]:px-10">
                    <div className="text-sm font-bold text-[#5C5C5C]">
                      {t("MobileAppInstall.codeLabel", "Your activation code")}
                    </div>
                    <div className="mt-2.5 flex flex-col gap-2.5 min-[62rem]:w-fit min-[62rem]:max-w-full">
                      <div className="min-w-0 text-[28px] leading-[34px] min-[62rem]:text-[34px] min-[62rem]:leading-[40px] font-extrabold tracking-wider uppercase truncate text-[#141414]">
                        {code}
                      </div>
                      <button
                        className="w-full h-12 shrink-0 bg-primary text-white border-none rounded-lg font-bold tracking-wide uppercase cursor-pointer transition-opacity hover:opacity-90"
                        onClick={handleCopy}
                      >
                        {copyButtonText}
                      </button>
                    </div>
                    <div className="hidden min-[62rem]:block mt-3 text-sm text-[#5C5C5C]">
                      {t("MobileAppInstall.codeHint", "Type it in the app when you create your account.")}
                    </div>
                    {canOpenInApp && (
                      <>
                        <div className="flex items-center gap-3 my-3.5 text-sm text-[#5C5C5C]">
                          <span className="flex-1 h-px bg-gray-200" />
                          {t("MobileAppInstall.orIfInstalled", "or, if the app is installed")}
                          <span className="flex-1 h-px bg-gray-200" />
                        </div>
                        <a
                          className="flex items-center justify-center gap-2 h-[50px] shrink-0 rounded-lg border-[1.5px] border-primary text-[#141414] font-bold no-underline"
                          href={openInAppUrl}
                        >
                          {t("Open in app")}
                          <FontAwesomeIcon aria-hidden="true" className="text-primary text-sm" icon={faArrowUpRight} />
                        </a>
                      </>
                    )}
                  </div>
                )}
                {showCodePanel && showQrPanel && (
                  <div className="hidden min-[62rem]:flex shrink-0 items-center justify-center bg-gray-200 my-6 w-px">
                    <span className="shrink-0 w-9 h-9 rounded-full border border-gray-200 bg-white flex items-center justify-center text-[13px] text-[#5C5C5C]">
                      {t("MobileAppInstall.or", "or")}
                    </span>
                  </div>
                )}
                {showQrPanel && (
                  <div className="hidden min-[62rem]:flex shrink-0 items-center gap-6 py-6 pl-9 pr-10">
                    <QRCode
                      className="shrink-0"
                      errorLevel="H"
                      icon={userAppLogo}
                      size={170}
                      style={{ borderColor: "#e5e7eb", borderRadius: 12 }}
                      type="svg"
                      value={window.location.href}
                    />
                    <div className="w-[226px]">
                      <div className="text-lg font-bold text-[#141414] mb-2">
                        {t("MobileAppInstall.qrTitle", "Continue on your phone")}
                      </div>
                      <ol className="text-[#5C5C5C] text-sm pl-[18px] space-y-[5px] list-decimal">
                        <li>{t("MobileAppInstall.qrScan", "Scan with your phone camera")}</li>
                        <li>{t("MobileAppInstall.qrInstall", "Install the app from the store")}</li>
                        {deepLinkQuery && <li>{t("MobileAppInstall.qrActivate", "Tap Open in app to activate")}</li>}
                      </ol>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>

        {/* Steps Section */}
        <section className="pt-9 pb-10 px-4 min-[62rem]:pt-8 min-[62rem]:px-5 bg-[#F7F8FA]">
          <div className={`${useExpandedLayout ? "max-w-[1700px]" : "max-w-[1280px]"} mx-auto`}>
            <h2 className="text-[26px] leading-8 min-[62rem]:text-3xl min-[62rem]:leading-9 font-bold mb-6 text-center text-[#141414]">
              {t("How it works?")}
            </h2>

            <div
              className={`grid grid-cols-1 justify-items-center ${useExpandedLayout ? "gap-4 md:grid-cols-2 xl:grid-cols-4" : "gap-4 min-[62rem]:gap-6 xl:grid-cols-3"}`}
            >
              {steps.map((step) => (
                <DeepLinkStepCard
                  key={step.title}
                  bullets={step.bullets}
                  compact={useExpandedLayout}
                  icon={step.icon}
                  renderFooter={step.renderFooter}
                  title={step.title}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-4 px-5 min-[62rem]:py-3 min-[62rem]:px-6 flex gap-4">
          <button
            className="text-[#3F3F46] underline cursor-pointer bg-transparent border-none text-base min-[62rem]:text-[15px] p-0"
            onClick={() => showHtml("legalnotice")}
          >
            {t("Legal notice")}
          </button>
          <button
            className="text-[#3F3F46] underline cursor-pointer bg-transparent border-none text-base min-[62rem]:text-[15px] p-0"
            onClick={() => showHtml("privacypolicy")}
          >
            {t("Privacy policy")}
          </button>
          <button
            className="text-[#3F3F46] underline cursor-pointer bg-transparent border-none text-base min-[62rem]:text-[15px] p-0"
            onClick={() => showHtml("eula")}
          >
            {t("EULA")}
          </button>
        </footer>
      </div>
    </>
  );
}
